import axios from "axios";
import config from '../config';
import { createSingleFlight } from './refreshCoordinator';

const MICROSOFT_SSO_CORRELATION_KEY = 'microsoft_sso_browser_correlation';

let accessToken = '';
let csrfToken = '';
let sessionExpiredHandler = null;
let sessionExpiryNotified = false;

if (typeof window !== 'undefined') {
  // Remove tokens left by the pre-Iteration-3 runtime without deleting
  // unrelated application preferences.
  window.localStorage.removeItem('access_token');
  window.localStorage.removeItem('refresh_token');
}

function bytesToBase64Url(bytes) {
  let binary = '';
  bytes.forEach((value) => {
    binary += String.fromCharCode(value);
  });
  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

export function createMicrosoftSSOCorrelation() {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  const value = bytesToBase64Url(bytes);
  window.sessionStorage.setItem(MICROSOFT_SSO_CORRELATION_KEY, value);
  return value;
}

export function getMicrosoftSSOCorrelation() {
  return window.sessionStorage.getItem(MICROSOFT_SSO_CORRELATION_KEY) || '';
}

export function clearMicrosoftSSOCorrelation() {
  window.sessionStorage.removeItem(MICROSOFT_SSO_CORRELATION_KEY);
}

export function setAccessToken(value) {
  accessToken = String(value || '');
  if (accessToken) {
    sessionExpiryNotified = false;
  }
}

export function clearAccessToken() {
  accessToken = '';
}

export function setSessionExpiredHandler(handler) {
  sessionExpiredHandler = typeof handler === 'function' ? handler : null;
  return () => {
    if (sessionExpiredHandler === handler) {
      sessionExpiredHandler = null;
    }
  };
}

function captureSessionMetadata(data) {
  if (data?.csrf_token) {
    csrfToken = String(data.csrf_token);
  }
}

function notifySessionExpired(reason = 'session_expired') {
  clearAccessToken();
  csrfToken = '';
  if (sessionExpiryNotified) return;
  sessionExpiryNotified = true;
  sessionExpiredHandler?.(reason);
}

const api = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const sessionClient = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const NO_REFRESH_PATHS = [
  '/v1/accounts/login/',
  '/v1/accounts/login/verify/',
  '/v1/accounts/login/resend-otp/',
  '/v1/accounts/activation/',
  '/v1/accounts/activate/',
  '/v1/accounts/session/csrf/',
  '/v1/accounts/token/refresh/',
  '/v1/accounts/logout/',
  '/v1/accounts/sso/microsoft/',
];

function requestPath(requestConfig = {}) {
  try {
    return new URL(
      requestConfig.url || '',
      requestConfig.baseURL || config.API_BASE_URL,
    ).pathname;
  } catch {
    return String(requestConfig.url || '');
  }
}

function mustNotRefresh(requestConfig) {
  const path = requestPath(requestConfig);
  return NO_REFRESH_PATHS.some((prefix) => path.includes(prefix));
}

async function fetchCsrfToken() {
  const response = await sessionClient.get('/v1/accounts/session/csrf/');
  captureSessionMetadata(response.data);
  if (!csrfToken) {
    throw new Error('CSRF token was not returned by the API.');
  }
  return csrfToken;
}

async function performRefresh() {
  const token = csrfToken || await fetchCsrfToken();
  const response = await sessionClient.post(
    '/v1/accounts/token/refresh/',
    {},
    { headers: { 'X-CSRFToken': token } },
  );
  captureSessionMetadata(response.data);
  setAccessToken(response.data?.access);
  if (!accessToken) {
    throw new Error('Access token was not returned by the API.');
  }
  return response.data;
}

export const refreshSessionSingleFlight = createSingleFlight(performRefresh);

api.interceptors.request.use(
  (requestConfig) => {
    if (accessToken) {
      requestConfig.headers = requestConfig.headers || {};
      requestConfig.headers.Authorization = `Bearer ${accessToken}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    captureSessionMetadata(response.data);
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    if (
      error.response?.status !== 401 ||
      originalRequest._authRefreshRetried ||
      mustNotRefresh(originalRequest)
    ) {
      return Promise.reject(error);
    }

    originalRequest._authRefreshRetried = true;
    try {
      const refreshed = await refreshSessionSingleFlight();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${refreshed.access}`;
      return api(originalRequest);
    } catch (refreshError) {
      notifySessionExpired('refresh_failed');
      return Promise.reject(refreshError);
    }
  },
);

export const authAPI = {
  register: (data) => api.post('/v1/accounts/register/', data),
  loginInit: (data) => api.post('/v1/accounts/login/', data),
  loginVerify: (data) => api.post('/v1/accounts/login/verify/', data),
  resendOTP: (loginChallenge) =>
    api.post('/v1/accounts/login/resend-otp/', {
      login_challenge: loginChallenge,
    }),
  resendActivation: (email) =>
    api.post('/v1/accounts/activation/resend/', { email }),
  getProfile: () => api.get('/v1/accounts/profile/'),
  changePassword: async (data) => {
    const response = await api.post('/v1/accounts/change-password/', data);
    if (response.data?.reauthentication_required) {
      notifySessionExpired('credentials_changed');
    }
    return response;
  },
  revokeAllSessions: async () => {
    const response = await api.post('/v1/accounts/sessions/revoke-all/', {});
    if (response.data?.reauthentication_required) {
      notifySessionExpired('sessions_revoked');
    }
    return response;
  },

  bootstrapSession: () => refreshSessionSingleFlight(),

  logout: async () => {
    try {
      const token = csrfToken || await fetchCsrfToken();
      return await sessionClient.post(
        '/v1/accounts/logout/',
        {},
        { headers: { 'X-CSRFToken': token } },
      );
    } finally {
      clearAccessToken();
      csrfToken = '';
    }
  },

  exchangeMicrosoftSSO: (code, browserCorrelation) =>
    api.post('/v1/accounts/sso/microsoft/exchange/', {
      code,
      browser_correlation: browserCorrelation,
    }),

  startMicrosoftSSO: (browserCorrelation) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${config.API_BASE_URL}v1/accounts/sso/microsoft/start/`;
    form.style.display = 'none';

    const correlationInput = document.createElement('input');
    correlationInput.type = 'hidden';
    correlationInput.name = 'browser_correlation';
    correlationInput.value = browserCorrelation;
    form.appendChild(correlationInput);

    document.body.appendChild(form);
    form.submit();
  },
};

export const identityAPI = {
  getSetupStatus: () => api.get('/v1/accounts/identity/setup/'),

  startMicrosoftBootstrap: (payload) =>
    api.post('/v1/accounts/sso/microsoft/bootstrap/start/', payload),

  getMicrosoftDirectory: () =>
    api.get('/v1/accounts/identity/microsoft/directory/'),

  getMicrosoftDirectoryHealth: () =>
    api.get('/v1/accounts/identity/microsoft/health/'),

  getMicrosoftDirectorySetupChecklist: () =>
    api.get('/v1/accounts/identity/microsoft/setup-checklist/'),

  updateMicrosoftDirectorySetupConfirmation: (key, confirmed, note = '') =>
    api.patch('/v1/accounts/identity/microsoft/setup-checklist/', {
      key,
      confirmed,
      note,
    }),

  getMicrosoftDirectorySyncConfig: () =>
    api.get('/v1/accounts/identity/microsoft/sync/config/'),

  updateMicrosoftDirectorySyncConfig: (modeOrPayload, enabled = true) => {
    const payload = typeof modeOrPayload === 'string'
      ? { mode: modeOrPayload, enabled }
      : { ...(modeOrPayload || {}) };
    return api.patch('/v1/accounts/identity/microsoft/sync/config/', payload);
  },

  triggerMicrosoftDirectorySync: () =>
    api.post('/v1/accounts/identity/microsoft/sync/trigger/', {}),

  getMicrosoftDirectorySyncRuns: () =>
    api.get('/v1/accounts/identity/microsoft/sync/runs/'),

  getMicrosoftDirectorySyncRun: (runId) =>
    api.get(`/v1/accounts/identity/microsoft/sync/runs/${runId}/`),

  syncMicrosoftGroups: (groupIds) =>
    api.post('/v1/accounts/identity/microsoft/sync/groups/', {
      group_ids: groupIds,
    }),

  syncMicrosoftUsers: (users) =>
    api.post('/v1/accounts/identity/microsoft/sync/users/', {
      users,
    }),

  syncMicrosoftAssignedApp: () =>
    api.post('/v1/accounts/identity/microsoft/sync/assigned-app/'),

  enableMicrosoftDirectoryUser: (directoryUserId, groupIds) =>
    api.post(`/v1/accounts/identity/microsoft/directory-users/${directoryUserId}/enable/`, {
      group_ids: groupIds,
    }),

  createMicrosoftGroupMapping: (directoryGroup, piiGroup) =>
    api.post('/v1/accounts/identity/microsoft/group-mappings/', {
      directory_group: directoryGroup,
      pii_group: piiGroup,
    }),

  deleteMicrosoftGroupMapping: (mappingId) =>
    api.delete(`/v1/accounts/identity/microsoft/group-mappings/${mappingId}/`),

  applyMicrosoftGroupMappings: () =>
    api.post('/v1/accounts/identity/microsoft/group-mappings/apply/'),
};

export const orgAPI = {
  registerOrganization: (payload) => api.post('/v1/accounts/register/', payload),
};


export const platformAdminAPI = {
  listOnboardingRequests: (params = {}) =>
    api.get('/v1/accounts/platform-admin/onboarding-requests/', { params }),

  getOnboardingRequest: (requestId) =>
    api.get(`/v1/accounts/platform-admin/onboarding-requests/${requestId}/`),

  approveOnboardingRequest: (requestId) =>
    api.post(`/v1/accounts/platform-admin/onboarding-requests/${requestId}/approve/`, {
      confirm: true,
    }),

  rejectOnboardingRequest: (requestId, rejectionReason) =>
    api.post(`/v1/accounts/platform-admin/onboarding-requests/${requestId}/reject/`, {
      rejection_reason: rejectionReason,
    }),
};

export const accountsAPI = {
  validateActivation: (uidb64, token) => api.get(`/v1/accounts/activate/${uidb64}/${token}/`),
  completeActivation: (payload) => api.post('/v1/accounts/activate/complete/', payload),
};

export const accessControlAPI = {
  getMyPermissions: () => api.get('/v1/accounts/me/permissions/'),

  getPermissions: () => api.get('/v1/accounts/admin/permissions/'),

  getAdminUsers: () => api.get('/v1/accounts/admin/users/'),

  createAdminUser: (payload) => api.post('/v1/accounts/admin/users/', payload),

  updateAdminUserStatus: (userId, isActive) =>
    api.patch(`/v1/accounts/admin/users/${userId}/status/`, {
      is_active: isActive,
    }),

  resendActivation: (userId) =>
    api.post(`/v1/accounts/admin/users/${userId}/resend-activation/`),

  getGroups: () => api.get('/v1/accounts/admin/groups/'),

  createGroup: (payload) => api.post('/v1/accounts/admin/groups/', payload),

  getGroupDetail: (groupId) => api.get(`/v1/accounts/admin/groups/${groupId}/`),

  updateGroup: (groupId, payload) =>
    api.put(`/v1/accounts/admin/groups/${groupId}/`, payload),

  deleteGroup: (groupId) =>
    api.delete(`/v1/accounts/admin/groups/${groupId}/`),

  setGroupPermissions: (groupId, permissionCodes) =>
    api.post(`/v1/accounts/admin/groups/${groupId}/permissions/`, {
      permission_codes: permissionCodes,
    }),

  addGroupMembers: (groupId, userIds) =>
    api.post(`/v1/accounts/admin/groups/${groupId}/members/`, {
      user_ids: userIds,
    }),

  getGroupMemberCandidates: (groupId, search = '') =>
    api.get(`/v1/accounts/admin/groups/${groupId}/member-candidates/`, {
      params: { search },
    }),

  addGroupMemberCandidates: (groupId, candidates) =>
    api.post(`/v1/accounts/admin/groups/${groupId}/member-candidates/`, {
      candidates,
    }),

  removeGroupMember: (groupId, userId) =>
    api.delete(`/v1/accounts/admin/groups/${groupId}/members/${userId}/`),
};

// PII APIs
export const piiAPI = {
  getDashboardStats: () => api.get('/pii/dashboard/'),

  getRecords: (params) => api.get('/pii/records/', { params }),

  getRecordDetail: (id) => api.get(`/pii/records/${id}/`),

  getRecordFields: (recordUuid) =>
    api.get(`/pii/pii-records/${recordUuid}/fields/`),

  requestPIIView: (recordUuid, payload = {}) =>
    api.post(`/pii/pii-records/${recordUuid}/request-view/`, payload),
};


export const platformIdentityAPI = {
  getPrincipals: (params) => api.get('/pii/principals/', { params }),

  getPrincipalDetail: (userUuid) =>
    api.get(`/pii/principals/${encodeURIComponent(userUuid)}/`),
};

// Document APIs
export const documentAPI = {
  requestView: (documentId) => api.post(`v1/documents/${documentId}/request-view/`),
  getDocumentPage: (sessionToken, pageNumber) => 
    api.get(`/documents/view/${sessionToken}/page/${pageNumber}/`),
  closeSession: (sessionToken) => api.post(`/documents/view/${sessionToken}/close/`),
};

export const ropaAPI = {
  getActivities: (params) => api.get('/pii/ropa/activities/', { params }),

  createActivity: (payload) => api.post('/pii/ropa/activities/', payload),

  getActivity: (activityId) =>
    api.get(`/pii/ropa/activities/${activityId}/`),

  updateActivity: (activityId, payload) =>
    api.patch(`/pii/ropa/activities/${activityId}/`, payload),

  deactivateActivity: (activityId) =>
    api.delete(`/pii/ropa/activities/${activityId}/`),

  getRecordLinks: (activityId) =>
    api.get(`/pii/ropa/activities/${activityId}/record-links/`),

  autoMapRecords: (activityId, payload) =>
    api.post(`/pii/ropa/activities/${activityId}/auto-map/`, payload),
};

export const auditAPI = {
  getSummary: () => api.get('/audit/summary/'),

  getLogs: (params) =>
    api.get('/audit/logs/', { params }),

  getLogDetail: (eventId) =>
    api.get(`/audit/logs/${eventId}/`),

  getLogEvidence: (eventId) =>
    api.get(`/audit/logs/${eventId}/evidence/`),

  getHealth: () => api.get('/audit/health/'),
};

export const platformPrincipalAPI = {
  list: (params) =>
    api.get('/pii/platform-principals/', { params }),

  detail: (principalId) =>
    api.get(`/pii/platform-principals/${principalId}/`),
};


export const revocationAPI = {
  // List all runs for the logged-in tenant
  listRuns: () => api.get('/pii/revocations/runs/'),

  // Get one run detail
  getRun: (id) => api.get(`/pii/revocations/runs/${id}/`),

  // Manually trigger a run (returns { run_id })
  triggerRun: () => api.post('/pii/revocations/runs/trigger/', {}),

  listLegalHolds: () => api.get('/pii/revocations/legal-holds/'),
  createLegalHold: (payload) => api.post('/pii/revocations/legal-holds/', payload),
  releaseLegalHold: (holdId, reason = '') =>
    api.post(`/pii/revocations/legal-holds/${holdId}/release/`, { reason }),
};

export default api;